/**
 * Générateur — Il lit, interprète et représente des données sous forme
 * d'histogrammes pour des classes de même amplitude.
 * Attendu ID : 213 | Niveau : 3ème | Domaine : Données (d14, O1)
 *
 * Source doc prof (OGD FONCTIONS 3e, séance B2S2). Plan de cartes auto-validé
 * 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « manquant »  — effectif manquant par le total + cumul + pourcentage
 *   « lecture »   — lire un histogramme (SVG auto-descriptif <desc>)
 *   « choix »     — quel histogramme représente la série ? (QCM images)
 *   « modale »    — l'intervalle le plus représenté (QCM, max UNIQUE)
 *   « partage »   — dans quelle classe tombe la valeur qui partage la série ?
 *
 * Adaptation signalée : « représente la série par un histogramme »
 * (construction) n'a pas d'interactif → QCM d'histogrammes (l'élève CHOISIT
 * la bonne représentation, distracteurs = hauteurs permutées/décalées).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou,
} from '../../../utils';
import { imgElement, imgChoix, tableEnonce, ligneTable } from '../../5eme/statistiques/svgStats';
import { svgHisto, somme } from './donnees';

const ATTENDU_ID = 213;

const L_MANQUANT = 'Retrouver l\'effectif manquant d\'un histogramme';
const L_LECTURE = 'Lire un histogramme';
const L_CHOIX = 'Choisir l\'histogramme qui représente la série';
const L_MODALE = 'Repérer l\'intervalle le plus représenté';
const L_PARTAGE = 'Partager une série en classes en deux moitiés';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Contextes (grandeur, unité, amplitude, borne basse, pas d'axe y)
const CONTEXTES = [
    { sujet: 'les notes sur 100 des élèves d\'un collège au brevet blanc', unite: 'points', amp: 20, lo0: 0, yStep: 10 },
    { sujet: 'les performances au lancer de poids d\'un groupe d\'élèves', unite: 'm', amp: 2, lo0: 5, yStep: 5 },
    { sujet: 'les pulsations cardiaques au repos des élèves de 3e', unite: 'pulsations', amp: 4, lo0: 54, yStep: 10 },
    { sujet: 'les âges des skieurs interrogés par une station', unite: 'ans', amp: 10, lo0: 0, yStep: 10 },
    { sujet: 'le temps d\'écran quotidien d\'élèves de 3e', unite: 'h', amp: 1, lo0: 0, yStep: 10 },
];

// Série en classes : effectifs construits pour un TOTAL rond (pourcentages entiers).
const tirerSerie = (nbClasses, total) => {
    // Répartition en pourcentages entiers multiples de 2 sommant à 100.
    for (let essai = 0; essai < 60; essai++) {
        const pcts = [];
        let reste = 50;                                    // en demi-pourcents ×2
        for (let i = 0; i < nbClasses - 1; i++) {
            const maxPart = reste - (nbClasses - 1 - i);
            const p = randInt(1, Math.max(1, Math.min(maxPart, 20)));
            pcts.push(p * 2); reste -= p;
        }
        pcts.push(reste * 2);
        if (pcts.every(p => p >= 2) && (total * Math.min(...pcts)) % 100 === 0) {
            return pcts.map(p => (total * p) / 100);
        }
    }
    return Array(nbClasses).fill(total / nbClasses);       // repli uniforme (total divisible)
};

const libClasse = (ctx, i) => `[${ctx.lo0 + i * ctx.amp} ; ${ctx.lo0 + (i + 1) * ctx.amp}[`;

const tableSerie = (ctx, effectifs, manquantIdx = -1) => tableEnonce(
    ligneTable('Classe', effectifs.map((_, i) => libClasse(ctx, i))),
    ligneTable('Effectif', effectifs.map((v, i) => (i === manquantIdx ? 'N' : String(v)))),
);

// ═════════ « manquant » — N par le total, cumul, pourcentage ═════════

const genManquantTrous = () => {
    const ctx = randomChoice(CONTEXTES);
    const total = randomChoice([100, 150, 200, 300]);
    const eff = tirerSerie(5, total);
    const idx = randInt(1, 3);
    // Seuil de cumul : borne d'une classe ≥ 2 → « moins de X »
    const kSeuil = randInt(2, 3);
    const seuil = ctx.lo0 + kSeuil * ctx.amp;
    const cumul = somme(eff.slice(0, kSeuil));
    // Entier PAR CONSTRUCTION : chaque effectif correspond à un pourcentage entier.
    const pctAu = ((total - cumul) * 100) / total;
    const phrase = `N = {{${eff[idx]}}} ; moins de ${seuil} : {{${cumul}}} ; au moins ${seuil} : {{${pctAu}}} %.`;
    const payload = makeTexteTrou(phrase,
        `<p>On a relevé ${ctx.sujet}, soit ${total} valeurs :</p>`
        + tableSerie(ctx, eff, idx)
        + `<p><b>Calcule l'effectif manquant N, le nombre de valeurs inférieures à ${seuil} ${ctx.unite}, `
        + `puis le pourcentage de valeurs d'au moins ${seuil} ${ctx.unite}.</b></p>`,
        { ...opts(L_MANQUANT), mode: 'saisie' });
    payload._meta.params = { ctx: ctx.sujet, total, eff, idx, seuil };
    return payload;
};

// ═════════ « lecture » — lire l'histogramme (SVG <desc>) ═════════

const genLectureTrous = () => {
    const ctx = randomChoice(CONTEXTES);
    const total = randomChoice([100, 200]);
    const eff = tirerSerie(5, total);
    const i1 = randInt(0, 2); const i2 = i1 + randInt(1, 2);
    const phrase = `Classe ${libClasse(ctx, i1)} : {{${eff[i1]}}} ; classe ${libClasse(ctx, i2)} : {{${eff[i2]}}} ; effectif total : {{${total}}}.`;
    const payload = makeTexteTrou(phrase,
        `<p>L'histogramme représente ${ctx.sujet}.</p>`
        + '<p><b>Lis les effectifs demandés, puis calcule l\'effectif total.</b></p>',
        { ...opts(L_LECTURE), mode: 'saisie' });
    payload.elements_additionnels = imgElement(svgHisto(ctx.lo0, ctx.amp, eff, { yStep: ctx.yStep, xLabel: ctx.unite }));
    payload._meta.params = { ctx: ctx.sujet, eff, i1, i2, total };
    return payload;
};

// ═════════ « choix » — quel histogramme représente la série ? (QCM images) ═════════

const genChoixQcm = () => {
    const ctx = randomChoice(CONTEXTES);
    const total = randomChoice([100, 200]);
    const eff = tirerSerie(4, total);
    // Distracteurs : permutation (≠ identité) et décalage d'une barre.
    let permute = eff;
    for (let essai = 0; essai < 30; essai++) {
        const p = shuffle([...eff]);
        if (p.join() !== eff.join()) { permute = p; break; }
    }
    if (permute.join() === eff.join()) permute = [...eff.slice(1), eff[0]];
    let decale = eff.map((v, i) => (i === 0 ? v + ctx.yStep : (i === 1 ? Math.max(2, v - ctx.yStep) : v)));
    if (decale.join() === permute.join()) decale = decale.map((v, i) => (i === 2 ? v + ctx.yStep : v));
    const bon = imgChoix(svgHisto(ctx.lo0, ctx.amp, eff, { yStep: ctx.yStep, compact: true }));
    const choix = [bon,
        imgChoix(svgHisto(ctx.lo0, ctx.amp, permute, { yStep: ctx.yStep, compact: true })),
        imgChoix(svgHisto(ctx.lo0, ctx.amp, decale, { yStep: ctx.yStep, compact: true }))];
    const payload = makeQcm(choix, bon,
        `<p>On a relevé ${ctx.sujet} :</p>` + tableSerie(ctx, eff)
        // « Vérifie chaque hauteur » donnait la méthode de comparaison, alors
        // que les distracteurs sont justement des hauteurs permutées.
        + '<p><b>Quel histogramme représente cette série ?</b></p>',
        opts(L_CHOIX));
    payload._meta.params = { ctx: ctx.sujet, eff, permute, decale };
    return payload;
};

// ═════════ « modale » — l'intervalle le plus représenté (QCM) ═════════

const genModaleQcm = () => {
    const ctx = randomChoice(CONTEXTES);
    let eff;
    for (let essai = 0; essai < 60; essai++) {
        eff = tirerSerie(5, randomChoice([100, 150, 200]));
        const max = Math.max(...eff);
        if (eff.filter(v => v === max).length === 1) break;
        eff = null;
    }
    if (!eff) eff = [10, 50, 60, 40, 40];                  // repli : max unique
    const iMax = eff.indexOf(Math.max(...eff));
    const correct = libClasse(ctx, iMax);
    const autres = eff.map((_, i) => i).filter(i => i !== iMax);
    const choix = [correct, ...shuffle(autres).slice(0, 3).map(i => libClasse(ctx, i))];
    const payload = makeQcm(choix, correct,
        `<p>L'histogramme représente ${ctx.sujet}.</p>`
        + '<p><b>Quel intervalle est le plus représenté ?</b></p>',
        opts(L_MODALE));
    payload.elements_additionnels = imgElement(svgHisto(ctx.lo0, ctx.amp, eff, { yStep: ctx.yStep, xLabel: ctx.unite }));
    payload._meta.params = { ctx: ctx.sujet, eff, iMax };
    return payload;
};

// ═════════ « partage » — la classe de la valeur qui coupe en deux (QCM) ═════════

const genPartageQcm = () => {
    const ctx = randomChoice(CONTEXTES);
    let eff; let rang; let iMed;
    for (let essai = 0; essai < 80; essai++) {
        // Effectifs libres à TOTAL IMPAIR : la (N+1)/2-ième valeur sépare la
        // série en deux groupes de même effectif (comme l'Ex.4 du doc, N = 31).
        eff = Array.from({ length: 5 }, () => randInt(3, 15));
        const total = somme(eff);
        if (total % 2 === 0) eff[randInt(0, 4)] += 1;
        rang = (somme(eff) + 1) / 2;
        // Classe contenant la rang-ième valeur — STRICTEMENT à l'intérieur
        // (pas à la frontière d'un cumul : la coupure serait discutable).
        let cumul = 0; iMed = -1;
        for (let i = 0; i < eff.length; i++) {
            if (rang > cumul && rang < cumul + eff[i]) { iMed = i; break; }
            cumul += eff[i];
        }
        if (iMed >= 1 && iMed <= 3) break;
        iMed = -1;
    }
    if (iMed < 0) { eff = [4, 10, 6, 5, 6]; rang = 16; iMed = 2; }   // repli (doc : cumuls 4, 14, 20)
    const correct = libClasse(ctx, iMed);
    const autres = eff.map((_, i) => i).filter(i => i !== iMed);
    const choix = [correct, ...shuffle(autres).slice(0, 3).map(i => libClasse(ctx, i))];
    const payload = makeQcm(choix, correct,
        `<p>On a relevé ${ctx.sujet} :</p>` + tableSerie(ctx, eff)
        // « Cumule les effectifs » était la technique attendue.
        + `<p>La série compte ${somme(eff)} valeurs. <b>Dans quelle classe se trouve la ${rang}e valeur, `
        + 'celle qui sépare la série en deux groupes de même effectif ?</b></p>',
        opts(L_PARTAGE));
    payload._meta.params = { ctx: ctx.sujet, eff, rang, iMed };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    manquant_texte_trou_saisie: genManquantTrous,
    lecture_texte_trou_saisie: genLectureTrous,
    choix_qcm_unique: genChoixQcm,
    modale_qcm_unique: genModaleQcm,
    partage_qcm_unique: genPartageQcm,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'manquant', label: L_MANQUANT, formatsList: buildFormats('manquant') },
    { id: 'lecture', label: L_LECTURE, formatsList: buildFormats('lecture') },
    { id: 'choix', label: L_CHOIX, formatsList: buildFormats('choix') },
    { id: 'modale', label: L_MODALE, formatsList: buildFormats('modale') },
    { id: 'partage', label: L_PARTAGE, formatsList: buildFormats('partage') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
